import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Settings } from 'lucide-react'
import { Feature, Project, SummaryConfig, deleteSummaryConfig } from '../../api/client'
import SummaryConfigEditor, { EditorSurface } from './SummaryConfigEditor'
import ConfirmationModal from '../ConfirmationModal'

/**
 * SummaryConfigMenu — the per-summary "gear" that replaces the old standalone
 * SummaryConfigList table. A cog button (sits to the right of a card's view
 * flipper) opens a small popover with Edit / Delete, and hosts the shared
 * SummaryConfigEditor modal for the edit path. One per summary card.
 *
 * Delete is a destructive hard-delete (the config and its summary_results
 * history are gone), so it confirms first. There is no archive/unarchive yet;
 * when that lands this should become a non-destructive Archive again.
 *
 * Motion vocab (CLAUDE.md): the cog uses whileHover scale 1.1 / whileTap scale
 * 0.9; the popover pops with the surface spring (scale 0.95→1 / y -8→0) and a
 * top-right transformOrigin, since it hangs off a right-aligned trigger.
 */

const SURFACE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const
const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const

interface SummaryConfigMenuProps {
  config: SummaryConfig
  /** Which surface the editor saves against (drives default source + pin). */
  surface: EditorSurface
  featureId?: number
  projectId?: number
  /** Sibling features for the editor's source / stratification pickers. */
  features?: Feature[]
  /** Selectable projects for the editor's project-tag picker (dashboard). */
  projects?: Project[]
  /** Notified after a successful edit / delete so the host can refresh. */
  onChanged?: () => void
}

const SummaryConfigMenu: React.FC<SummaryConfigMenuProps> = ({
  config,
  surface,
  featureId,
  projectId,
  features = [],
  projects = [],
  onChanged,
}) => {
  const [open, setOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const title = config.user_title?.trim()
  const summaryLabel = title ? `"${title}"` : 'this summary'

  // Close the popover on any outside click or Escape.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const confirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteSummaryConfig(config.id)
      setConfirmOpen(false)
      onChanged?.()
    } catch (e) {
      console.error('Failed to delete summary config', e)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <motion.button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Summary settings"
        aria-label="Summary settings"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={BUTTON_SPRING}
        className="rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800"
        data-testid="summary-config-menu-button"
      >
        <Settings size={18} strokeWidth={2} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={SURFACE_SPRING}
            style={{ transformOrigin: 'top right' }}
            role="menu"
            className="absolute right-0 z-20 mt-1 w-36 overflow-hidden rounded-md border border-gray-200 bg-white py-1 shadow-lg"
            data-testid="summary-config-menu"
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                setEditorOpen(true)
              }}
              className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
            >
              Edit
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                setConfirmOpen(true)
              }}
              className="block w-full px-3 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-rose-600"
            >
              Delete
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <SummaryConfigEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={() => onChanged?.()}
        surface={surface}
        featureId={featureId}
        projectId={projectId}
        features={features}
        projects={projects}
        existing={config}
      />

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => { if (!deleting) setConfirmOpen(false) }}
        onConfirm={confirmDelete}
        title="Delete Summary"
        message={`Are you sure you want to delete ${summaryLabel}? This permanently removes the summary and its config.`}
        confirmText="Delete"
        isLoading={deleting}
      />
    </div>
  )
}

export default SummaryConfigMenu
